// src/components/enterprise/SesgelioStudiosDashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Activity,
  ArrowLeft,
  Building2,
  ChevronRight,
  Cpu,
  Database,
  FileSpreadsheet,
  Key,
  Lock,
  LogOut,
  Mic,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingUp,
  Users,
  Copy,
  Check,
  ExternalLink,
  X
} from 'lucide-react';

const B2B_API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:4001'
    : 'https://sesgelio.com';

const formatBytes = (bytes: number) => {
  if (!bytes || bytes === 0) return '0 KB';
  const k = 1024;
  const sizes = ['Bayt', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const sanitizeInput = (val: string) => val.replace(/[<>"'{};$]/g, '');

// 🎨 3 NET TESLİMAT MODELİ (Gereksiz Şablon C kaldırıldı)
const TEMPLATE_META: Record<string, { name: string; desc: string; setupNote: string }> = {
  server_dm: {
    name: 'Model A: Şirkete Özel Kurumsal Mimari (OMNIO Modeli)',
    desc: 'Şirket içi metin/ses odaları, rol sistemi, otomatik ekip DM ve özel tasarım.',
    setupNote: 'Özel Tasarım & Kurulum Ücreti + Aylık Kullanım'
  },
  support_drawer: {
    name: 'Model B: Tek Odalı Canlı İletişim Kutusu (Hazır Widget)',
    desc: 'Web sitesi veya uygulama içinde tek odada canlı ses, görüntü, ekran paylaşımı ve sohbet.',
    setupNote: 'Kurulum Ücreti Yok (Tak-Çalıştır Hazır Şablon)'
  },
  headless_sdk: {
    name: 'Model C: Sadece Altyapı (Headless API & SDK)',
    desc: 'Müşteri kendi ekran tasarımını kendisi kodlar; ses, mesaj ve dosya için API motorumuzu kullanır.',
    setupNote: 'Geliştirici Dökümanı (Docs) ile Teslim Edilir'
  }
};

// 🚀 SESGELİO KALİTE VE PERFORMANS PAKETLERİ (TIER)
const QUALITY_TIERS: Record<string, {
  name: string;
  audioSpec: string;
  videoSpec: string;
  uploadLimit: string;
  defaultSeatPrice: string;
  defaultStoragePrice: string;
  badgeClass: string;
}> = {
  STANDARD: {
    name: '🥉 Kurumsal Standart',
    audioSpec: '64 kbps Ses',
    videoSpec: '720p 30 FPS Ekran',
    uploadLimit: '50 MB / Dosya',
    defaultSeatPrice: '45',
    defaultStoragePrice: '25',
    badgeClass: 'bg-gray-500/15 text-gray-300 border-gray-500/30'
  },
  PREMIUM: {
    name: '🥈 Kurumsal PRO',
    audioSpec: '128 kbps HD Ses',
    videoSpec: '1080p 60 FPS Ekran',
    uploadLimit: '75 MB / Dosya',
    defaultSeatPrice: '75',
    defaultStoragePrice: '35',
    badgeClass: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'
  },
  ELITE: {
    name: '🥇 Kurumsal VIP (Elite)',
    audioSpec: '256 kbps Stüdyo Ses',
    videoSpec: '2K (1440p) 60 FPS Ekran',
    uploadLimit: '100 MB / Dosya',
    defaultSeatPrice: '120',
    defaultStoragePrice: '50',
    badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30'
  }
};

// Partnerin kayıtlı templateId bilgisinden Şablon ve Kalite Paketini ayıklar
const parsePartnerTemplateAndTier = (rawTemplateId?: string) => {
  const parts = (rawTemplateId || 'server_dm__ELITE').split('__');
  const tplKey = TEMPLATE_META[parts[0]] ? parts[0] : 'server_dm';
  const tierKey = QUALITY_TIERS[parts[1]] ? parts[1] : 'ELITE';
  return { tplKey, tierKey, tpl: TEMPLATE_META[tplKey], tier: QUALITY_TIERS[tierKey] };
};

// 🧭 EKRAN YÖNLENDİRME TİPLERİ (Her şey ayrı ekranda!)
type StudiosScreen =
  | 'MAIN_MENU'         // 1. Giriş sonrası gelen API vs Enterprise seçim menüsü
  | 'ENTERPRISE_SOON'   // 2. Enterprise "Yakında Gelecek" ekranı
  | 'API_PARTNERS'      // 3. API Partnerleri (OMNIO, Dış Firmalar) listesi
  | 'PARTNER_COMPANIES' // 4. Seçili partnerin altındaki şirketler listesi
  | 'COMPANY_USAGE'     // 5. Seçili şirketin detaylı kullanım ve hakediş ekranı
  | 'COMPANY_LOGS'      // 6. Şirket ve Kullanıcı Bazlı Denetim & Güvenlik Logları Ekranı
  | 'API_DOCS';         // 7. Kendi Tasarımını Yapacak Müşteriler İçin Hazır Geliştirici Dökümanı (Docs)

export const SesgelioStudiosDashboard: React.FC = () => {
  // Güvenlik State'leri
  const [studiosToken, setStudiosToken] = useState<string | null>(() => sessionStorage.getItem('studios_master_token'));
  const [loginUser, setLoginUser] = useState<string>('');
  const [loginPass, setLoginPass] = useState<string>('');
  const [loginPin, setLoginPin] = useState<string>('');
  const [honeypot, setHoneypot] = useState<string>('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [devToolsDetected, setDevToolsDetected] = useState<boolean>(false);

  // Navigasyon & Veri State'leri
  const [currentScreen, setCurrentScreen] = useState<StudiosScreen>('MAIN_MENU');
  const [partners, setPartners] = useState<any[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

 

  // 📋 KURUCU LOG MERKEZİ STATE'LERİ (Şirket + Kullanıcı Bazlı Ayrım)
  const [founderLogs, setFounderLogs] = useState<any[]>([]);
  const [companyUsersForLogs, setCompanyUsersForLogs] = useState<any[]>([]);
  const [selectedLogUserId, setSelectedLogUserId] = useState<string>('ALL');
  const [selectedLogCategory, setSelectedLogCategory] = useState<string>('ALL');
  const [logsLoading, setLogsLoading] = useState<boolean>(false);

  const fetchFounderLogs = async (partnerId?: string | null, companyId?: string | null, userIdFilter = 'ALL', categoryFilter = 'ALL') => {
    if (!studiosToken) return;
    setLogsLoading(true);
    try {
      const q = new URLSearchParams();
      if (partnerId) q.set('partnerId', partnerId);
      if (companyId) q.set('companyId', companyId);
      if (userIdFilter && userIdFilter !== 'ALL') q.set('userId', userIdFilter);
      if (categoryFilter && categoryFilter !== 'ALL') q.set('category', categoryFilter);

      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/audit-logs?${q.toString()}`, {
        headers: { Authorization: `Bearer ${studiosToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFounderLogs(data.logs || []);
        if (companyId) setCompanyUsersForLogs(data.companyUsers || []);
      }
    } catch (e) {
      console.error('Loglar çekilemedi');
    } finally {
      setLogsLoading(false);
    }
  };

// Yeni Partner Modalı
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newPartnerName, setNewPartnerName] = useState<string>('');
  const [newBrandTitle, setNewBrandTitle] = useState<string>('');
  const [newTemplateId, setNewTemplateId] = useState<string>('server_dm');
  const [newQualityTier, setNewQualityTier] = useState<string>('ELITE');
  const [newPrimaryColor, setNewPrimaryColor] = useState<string>('#6366f1');
  const [newUserPrice, setNewUserPrice] = useState<string>('120');
  const [newStoragePrice, setNewStoragePrice] = useState<string>('50');

  // 🛡️ F12, SAĞ TIK VE DEVTOOLS KALKANI
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'i', 'J', 'j', 'C', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u', 'S', 's'].includes(e.key)) ||
        (e.metaKey && e.altKey && ['I', 'i', 'J', 'j', 'U', 'u'].includes(e.key))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const checkDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      if (widthThreshold || heightThreshold) {
        setDevToolsDetected(true);
        sessionStorage.removeItem('studios_master_token');
        setStudiosToken(null);
        setPartners([]);
      } else {
        setDevToolsDetected(false);
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    const radarInterval = setInterval(checkDevTools, 1000);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
      clearInterval(radarInterval);
    };
  }, []);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoggingIn(true);

    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: sanitizeInput(loginUser.trim()),
          password: loginPass,
          pin: loginPin.replace(/\D/g, ''),
          honeypot
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setLoginError(data.error || 'Giriş reddedildi!');
      } else {
        sessionStorage.setItem('studios_master_token', data.token);
        setStudiosToken(data.token);
        setCurrentScreen('MAIN_MENU');
        setLoginPass('');
        setLoginPin('');
      }
    } catch (err) {
      setLoginError('B2B API sunucusuna (Port 4001) ulaşılamadı.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    if (studiosToken) {
      fetch(`${B2B_API_BASE}/api/b2b/studios/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${studiosToken}` }
      }).catch(() => {});
    }
    sessionStorage.removeItem('studios_master_token');
    setStudiosToken(null);
    setPartners([]);
    setCurrentScreen('MAIN_MENU');
  };

  const fetchTelemetry = async () => {
    if (!studiosToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/telemetry`, {
        headers: { Authorization: `Bearer ${studiosToken}` }
      });

      if (res.status === 401 || res.status === 403) {
        handleLogout();
        return;
      }

      if (res.ok) {
        const data = await res.json();
        setPartners(data.partners || []);
      }
    } catch (err) {
      console.error('Veri çekilemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studiosToken) {
      fetchTelemetry();
    }
  }, [studiosToken]);

  // 🗑️ GERÇEK VERİTABANINDAN ŞİRKET SİLME FONKSİYONU
  const handleDeleteCompany = async (companyId: string, companyName: string) => {
    if (!window.confirm(`"${companyName}" şirketini ve tüm verilerini MySQL veritabanından kalıcı olarak silmek istediğine emin misin?`)) {
      return;
    }
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/companies/${companyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${studiosToken}` }
      });
      if (res.ok) {
        await fetchTelemetry();
        if (selectedCompanyId === companyId) {
          setSelectedCompanyId(null);
          setCurrentScreen('PARTNER_COMPANIES');
        }
      }
    } catch (err) {
      alert('Silme işlemi başarısız.');
    }
  };

  const handleCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPartnerName.trim() || !studiosToken) return;

    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${studiosToken}`
        },
        body: JSON.stringify({
name: sanitizeInput(newPartnerName.trim()),
          brandTitle: sanitizeInput(newBrandTitle.trim() || `${newPartnerName.trim()} İletişim`),
          templateId: `${newTemplateId}__${newQualityTier}`,
          primaryColor: newPrimaryColor,
          bgColor: '#090a0f',
          baseMonthlyFee: 0,
          pricePerStorageGb: Number(newStoragePrice),
          pricePerTrafficGb: 10,
          pricePerVoiceMin: 0.35,
          pricePerActiveUser: Number(newUserPrice)
        })
      });

      if (res.ok) {
        setShowCreateModal(false);
        setNewPartnerName('');
        setNewBrandTitle('');
        await fetchTelemetry();
      }
    } catch (err) {
      alert('Partner oluşturulamadı.');
    }
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // 🚨 F12 AÇILIRSA EKRANI KİLİTLE
  if (devToolsDetected) {
    return (
      <div className="min-h-screen w-full bg-[#050508] text-white flex flex-col items-center justify-center p-6 select-none text-center">
        <div className="w-20 h-20 rounded-3xl bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-500 mb-4 animate-pulse">
          <ShieldAlert size={42} />
        </div>
        <h1 className="text-xl font-black uppercase text-red-400">SİBER KALKAN DEVREDE</h1>
        <p className="text-xs text-gray-400 max-w-md mt-2">
          Geliştirici Araçları (F12) algılandığı için güvenlik protokolü gereği oturum kapatıldı.
        </p>
      </div>
    );
  }

  // 🔒 EKRAN 0: 3 AŞAMALI GİRİŞ EKRANI
  if (!studiosToken) {
    return (
      <div className="min-h-screen w-full bg-[#050508] text-white flex items-center justify-center p-4 select-none relative overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#ff2b5e]/10 blur-[120px] pointer-events-none" />
        <form
          onSubmit={handleAdminLogin}
          autoComplete="off"
          className="w-full max-w-md bg-[#0b0c12]/95 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl relative z-10 space-y-5"
        >
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-[#ff2b5e] to-indigo-600 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(255,43,94,0.4)]">
            <Lock size={30} className="text-white" />
          </div>

          <div className="text-center">
            <h1 className="text-xl font-black tracking-tight uppercase">SESGELİO STUDIOS</h1>
            <p className="text-[11px] text-gray-400 mt-1">
              Yönetim & Telemetri Merkezi — Yetkili Kurucu Girişi
            </p>
          </div>

          <input
            type="text"
            name="website_url_trap"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
          />

          {loginError && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold text-center">
              {loginError}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">
                1. Yönetici Kullanıcı Adı
              </label>
              <input
                type="text"
                required
                maxLength={40}
                autoComplete="off"
                spellCheck={false}
                placeholder="Kurucu Kimliği"
                value={loginUser}
                onChange={e => setLoginUser(sanitizeInput(e.target.value))}
                className="w-full bg-black/60 border border-white/10 focus:border-[#ff2b5e] rounded-xl px-4 py-3 text-sm text-white outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">
                2. Yönetici Şifresi
              </label>
              <input
                type="password"
                required
                maxLength={80}
                autoComplete="new-password"
                placeholder="••••••••••••"
                value={loginPass}
                onChange={e => setLoginPass(e.target.value)}
                className="w-full bg-black/60 border border-white/10 focus:border-[#ff2b5e] rounded-xl px-4 py-3 text-sm text-white outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-amber-400 uppercase tracking-wider mb-1">
                3. Master Güvenlik PIN Kodu
              </label>
              <input
                type="password"
                required
                maxLength={8}
                inputMode="numeric"
                autoComplete="off"
                placeholder="6 Haneli PIN"
                value={loginPin}
                onChange={e => setLoginPin(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-black/60 border border-amber-500/30 focus:border-amber-400 rounded-xl px-4 py-3 text-center tracking-[0.5em] font-mono text-base text-amber-300 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoggingIn}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#ff2b5e] to-rose-600 hover:opacity-95 text-white font-black text-xs uppercase tracking-widest shadow-[0_0_25px_rgba(255,43,94,0.4)] transition-all active:scale-95"
          >
            {isLoggingIn ? 'Doğrulanıyor...' : 'Mührü Doğrula ve Giriş Yap'}
          </button>
        </form>
      </div>
    );
  }

const selectedPartner = partners.find(p => p.id === selectedPartnerId) || null;
  const selectedCompany = selectedPartner?.companies?.find((c: any) => c.id === selectedCompanyId) || null;

  // 🔒 ŞİRKETİN PAKETİNİ DOĞRUDAN MYSQL VERİTABANINDA GÜNCELLEME FONKSİYONU
  const handleUpdateCompanyPlan = async (companyId: string, newPlan: string) => {
    if (!studiosToken) return;
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/studios/companies/${companyId}/plan`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${studiosToken}`
        },
        body: JSON.stringify({ plan: newPlan })
      });
      if (res.ok) {
        await fetchTelemetry();
      } else {
        alert('Şirket paketi güncellenemedi.');
      }
    } catch (e) {
      alert('Sunucu bağlantı hatası.');
    }
  };

  return (
    <div className="h-screen w-full bg-[#050508] text-white font-sans flex flex-col select-none overflow-y-auto">
      {/* SADE VE TEMİZ ÜST BAR */}
      <header className="h-16 px-8 border-b border-white/10 bg-[#0a0b10]/90 backdrop-blur-2xl flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-4">
          {currentScreen !== 'MAIN_MENU' && (
            <button
              onClick={() => {
                if (currentScreen === 'COMPANY_USAGE' || currentScreen === 'COMPANY_LOGS') setCurrentScreen('PARTNER_COMPANIES');
                else if (currentScreen === 'PARTNER_COMPANIES') setCurrentScreen('API_PARTNERS');
                else setCurrentScreen('MAIN_MENU');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold text-gray-300 transition-all"
            >
              <ArrowLeft size={15} /> Geri Dön
            </button>
          )}

          <div
            onClick={() => setCurrentScreen('MAIN_MENU')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#ff2b5e] to-indigo-600 flex items-center justify-center shadow-lg">
              <Cpu size={19} className="text-white" />
            </div>
            <div>
              <h1 className="text-sm font-black tracking-tight uppercase">SESGELİO STUDIOS</h1>
              <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                <ShieldCheck size={11} /> Güvenli Kurucu Oturumu
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchTelemetry}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold text-gray-300"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin text-indigo-400' : ''} />
            Verileri Yenile
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-xs font-bold"
          >
            <LogOut size={14} /> Çıkış
          </button>
        </div>
      </header>

      {/* ===================================================================== */}
      {/* 🧭 EKRAN 1: GİRİŞ SONRASI ANA SEÇİM MENÜSÜ (API vs ENTERPRISE)        */}
      {/* ===================================================================== */}
      {currentScreen === 'MAIN_MENU' && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 max-w-5xl mx-auto w-full">
          <div className="text-center mb-10">
            <span className="text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400">
              Sesgelio Studios Komuta Merkezi
            </span>
            <h2 className="text-3xl font-black mt-4 tracking-tight">Hangi Sistemi Yönetmek İstiyorsun?</h2>
            <p className="text-sm text-gray-400 mt-2">
              B2B API altyapısını veya bağımsız Sesgelio Enterprise kurumsal platformunu seçerek ilerleyin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {/* KART 1: API YÖNETİMİ */}
            <div
              onClick={() => setCurrentScreen('API_PARTNERS')}
              className="group relative p-8 rounded-3xl bg-gradient-to-b from-indigo-500/10 via-white/[0.02] to-transparent border border-indigo-500/30 hover:border-indigo-400 transition-all cursor-pointer flex flex-col justify-between shadow-2xl"
            >
              <div>
                <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform">
                  <Key size={28} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  Aktif • {partners.length} Platform Bağlı
                </span>
                <h3 className="text-2xl font-black mt-3">Sesgelio API (CPaaS)</h3>
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                  OMNIO ve dış yazılım şirketlerine verdiğimiz gömülü iletişim API'lerini, bağlı alt şirketleri, dosya/ses kullanımlarını ve Harcadıkça Kazan gelirlerini yönetin.
                </p>
              </div>

              <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-xs font-black text-indigo-300 group-hover:text-white">
                <span>API Yönetim Paneline Gir</span>
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* KART 2: SESGELİO ENTERPRISE */}
            <div
              onClick={() => setCurrentScreen('ENTERPRISE_SOON')}
              className="group relative p-8 rounded-3xl bg-gradient-to-b from-[#ff2b5e]/10 via-white/[0.02] to-transparent border border-[#ff2b5e]/30 hover:border-[#ff2b5e] transition-all cursor-pointer flex flex-col justify-between shadow-2xl"
            >
              <div>
                <div className="w-14 h-14 rounded-2xl bg-[#ff2b5e]/20 border border-[#ff2b5e]/30 flex items-center justify-center text-[#ff2b5e] mb-6 group-hover:scale-110 transition-transform">
                  <Building2 size={28} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  Bağımsız Kurumsal Platform
                </span>
                <h3 className="text-2xl font-black mt-3">Sesgelio Enterprise</h3>
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                  API'lerden bağımsız olarak doğrudan web sitemiz üzerinden kurumsal giriş yapılan, şirketlere özel gelişmiş Sesgelio Enterprise masaüstü ve web uygulaması yönetimi.
                </p>
              </div>

              <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-xs font-black text-[#ff2b5e] group-hover:text-white">
                <span>Enterprise Alanını Görüntüle</span>
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 🚀 EKRAN 2: SESGELİO ENTERPRISE "YAKINDA GELECEK" EKRANI              */}
      {/* ===================================================================== */}
      {currentScreen === 'ENTERPRISE_SOON' && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center max-w-2xl mx-auto">
          <div className="w-20 h-20 rounded-3xl bg-[#ff2b5e]/15 border border-[#ff2b5e]/30 flex items-center justify-center text-[#ff2b5e] mb-6 shadow-[0_0_40px_rgba(255,43,94,0.25)]">
            <Rocket size={40} />
          </div>
          <span className="text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
            Yakında Gelecek
          </span>
          <h2 className="text-3xl font-black mt-4">Sesgelio Enterprise Komuta Merkezi</h2>
          <p className="text-sm text-gray-400 mt-3 leading-relaxed">
            Burası API hizmetlerinden tamamen bağımsız olarak; doğrudan kendi web sitemiz üzerinden kurumsal hesapla giriş yapılan <b>Sesgelio Enterprise</b> özel uygulamamızın yönetim, lisanslama ve şirket analitiği ekranı olacaktır.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full mt-8 text-left">
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="text-xs font-black text-white mb-1">🏢 Kurumsal Lisanslar</div>
              <p className="text-[11px] text-gray-400">Doğrudan Enterprise paketi satın alan şirketlerin yönetimi.</p>
            </div>
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="text-xs font-black text-white mb-1">🛡️ Özel Sunucu & On-Prem</div>
              <p className="text-[11px] text-gray-400">Holdingler için izole sunucu ve departman hiyerarşisi takibi.</p>
            </div>
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="text-xs font-black text-white mb-1">📊 Kurumsal Analitik</div>
              <p className="text-[11px] text-gray-400">Enterprise uygulamasındaki aktif toplantı ve veri istatistikleri.</p>
            </div>
          </div>

          <button
            onClick={() => setCurrentScreen('MAIN_MENU')}
            className="mt-8 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-xs font-black uppercase tracking-wider"
          >
            ← Ana Menüye Dön
          </button>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 🔌 EKRAN 3: API PARTNERLERİ LİSTESİ (SADECE ANA PLATFORMLAR)          */}
      {/* ===================================================================== */}
      {currentScreen === 'API_PARTNERS' && (
        <div className="flex-1 p-8 max-w-6xl mx-auto w-full space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-black">API Verdiğimiz Platformlar & Partnerler</h2>
              <p className="text-xs text-gray-400 mt-1">
                Altındaki şirketleri görmek için platforma tıklayın veya kendi tasarımını yapacak müşteriler için <b>API Dökümanını</b> açın.
              </p>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setCurrentScreen('API_DOCS')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-xs font-black uppercase tracking-wider transition-all"
              >
                📘 Geliştirici Kılavuzu (API Docs)
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#ff2b5e] to-rose-600 text-xs font-black uppercase tracking-wider shadow-lg"
              >
                <Plus size={15} /> Yeni API Partneri Ekle
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {partners.map(p => {
              const { tpl, tier } = parsePartnerTemplateAndTier(p.templateId);
              return (
                <div
                  key={p.id}
                  onClick={() => {
                    setSelectedPartnerId(p.id);
                    setCurrentScreen('PARTNER_COMPANIES');
                  }}
                  className="p-6 rounded-3xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-indigo-500/40 transition-all cursor-pointer flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white shadow-md"
                          style={{ backgroundColor: p.primaryColor }}
                        >
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="text-base font-black">{p.name}</h3>
                          <span className="text-[11px] text-gray-400">{tpl.name}</span>
                        </div>
                      </div>

                      {p.isInternalProduct || p.name.toUpperCase().includes('OMNIO') ? (
                        <span className="text-[10px] px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-bold">
                          Kendi Ürünümüz (OMNIO)
                        </span>
                      ) : (
                        <span className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                          Dış API Müşterisi
                        </span>
                      )}
                    </div>

                    {/* KALİTE VE PERFORMANS PAKETİ ROZETLERİ */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-md border ${tier.badgeClass}`}>
                        {tier.name}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/5 text-gray-300 border border-white/10">
                        🎙️ {tier.audioSpec}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/5 text-gray-300 border border-white/10">
                        🖥️ {tier.videoSpec}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/5 text-gray-300 border border-white/10">
                        📁 {tier.uploadLimit}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-black/40 border border-white/5 flex items-center justify-between text-xs mt-4">
                      <span className="text-gray-400 font-mono truncate mr-2">API Key: {p.apiKey}</span>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          copyText(p.apiKey, p.id);
                        }}
                        className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-[11px] font-bold flex items-center gap-1 shrink-0"
                      >
                        {copiedKey === p.id ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                        Kopyala
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-white/10 text-xs">
                    <div>
                      <span className="text-[10px] text-gray-500 block">Kayıtlı Şirket</span>
                      <b className="text-sm text-white">{p.summary?.companyCount || 0} Firma</b>
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-500 block">Tarife</span>
                      <b className="text-sm text-emerald-400">{p.pricePerActiveUser} ₺/Kişi • {p.pricePerStorageGb} ₺/GB</b>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-gray-500 block">Şirketleri Yönet</span>
                      <span className="text-xs font-black text-indigo-400 group-hover:underline inline-flex items-center gap-1">
                        Listeyi Aç <ChevronRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 🏢 EKRAN 4: SEÇİLİ PARTNERİN ALTINDAKİ ŞİRKETLER LİSTESİ              */}
      {/* ===================================================================== */}
      {currentScreen === 'PARTNER_COMPANIES' && selectedPartner && (
        <div className="flex-1 p-8 max-w-6xl mx-auto w-full space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-xs text-indigo-400 font-bold mb-1">
                API Partneri: {selectedPartner.name}
              </div>
              <h2 className="text-2xl font-black">
                Bağlı Şirketler Listesi ({selectedPartner.companies?.length || 0})
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                Buradaki şirketler API üzerinden gerçek zamanlı oluşmuştur. Bir şirketin kullanım detaylarını görmek için <b>"Kullanım & Analiz"</b> butonuna tıklayın.
              </p>
            </div>

            <div className="relative w-72">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Şirket adı veya Vergi No ara..."
                value={searchQuery}
                onChange={e => setSearchQuery(sanitizeInput(e.target.value))}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {(selectedPartner.companies || [])
              .filter(
                (c: any) =>
                  c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (c.taxNumber && c.taxNumber.includes(searchQuery))
              )
              .map((comp: any) => (
                <div
                  key={comp.id}
                  className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 flex flex-wrap items-center justify-between gap-4 hover:bg-white/[0.04] transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center font-black text-indigo-300 text-base">
                      {comp.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white">{comp.name}</h3>
<div className="flex flex-wrap items-center gap-2.5 text-[11px] text-gray-400 mt-1">
                        <span>Vergi No: <b>{comp.taxNumber || comp.externalCompanyId}</b></span>
                        <span>•</span>
                        <span className="font-mono text-indigo-300">Kod: {comp.b2bConnectCode}</span>
                        <span>•</span>
                        <span><b>{comp.activeUsersCount}</b> Çalışan</span>
                        <span>•</span>
                        <span className="text-emerald-400 font-bold">
                          Hakediş: {comp.billing?.totalRevenue?.toLocaleString('tr-TR')} ₺
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* 📦 ŞİRKETE ÖZEL PAKET SEÇİMİ (DOĞRUDAN SQL'E YAZAR) */}
                    <select
                      value={(comp.plan || 'ELITE').toUpperCase()}
                      onChange={e => handleUpdateCompanyPlan(comp.id, e.target.value)}
                      className="bg-black/70 border border-white/15 focus:border-indigo-400 rounded-xl px-3 py-2 text-xs font-bold text-amber-300 outline-none cursor-pointer"
                      title="Bu şirketin MySQL'deki aktif kalite ve limit paketini değiştir"
                    >
                      <option value="NONE">🚫 Paketsiz (Buzlu Cam Kilitli)</option>
                      <option value="STANDARD">🥉 Standart (64kbps • 720p • 50MB)</option>
                      <option value="PREMIUM">🥈 PRO (128kbps • 1080p 60fps • 75MB)</option>
                      <option value="ELITE">🥇 VIP Elite (256kbps • 2K 60fps • 100MB)</option>
                    </select>
{/* KULLANIM DETAYI EKRANINA GEÇİŞ BUTONU */}
                    <button
                      onClick={() => {
                        setSelectedCompanyId(comp.id);
                        setCurrentScreen('COMPANY_USAGE');
                      }}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition-all"
                    >
                      <Activity size={14} /> Kullanımlar
                    </button>

                    {/* 📋 ŞİRKET VE KULLANICI BAZLI LOG EKRANI BUTONU */}
                    <button
                      onClick={() => {
                        setSelectedCompanyId(comp.id);
                        setSelectedLogUserId('ALL');
                        setSelectedLogCategory('ALL');
                        setCurrentScreen('COMPANY_LOGS');
                        fetchFounderLogs(selectedPartner.id, comp.id, 'ALL', 'ALL');
                      }}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 font-bold text-xs transition-all"
                    >
                      <FileSpreadsheet size={14} /> Log Kayıtları (Kullanıcı Bazlı)
                    </button>

{/* İLETİŞİM PANELİNİ ŞİFRELİ TOKEN İLE AÇMA BUTONU (URL'de VKN/API Key Görünmez!) */}
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${B2B_API_BASE}/api/b2b/sso-handshake`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              apiKey: selectedPartner.apiKey,
                              externalCompanyId: comp.externalCompanyId,
                              companyName: comp.name,
                              taxNumber: comp.taxNumber || comp.externalCompanyId,
                              externalUserId: 'omnio_user_1',
                              username: 'Ömer Onbaşı',
                              isCompanyAdmin: true,
                            })
                          });
const data = await res.json();
                          if (res.ok && data.token) {
                            const pSlug = selectedPartner.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                            const cSlug = comp.name.toLowerCase().replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                            window.open(`/embed/${pSlug}/${cSlug}?session=${encodeURIComponent(data.token)}`, '_blank');
                          } else {
                            alert(data.error || 'Oturum başlatılamadı.');
                          }
                        } catch (e) {
                          alert('Sunucu bağlantı hatası.');
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 font-bold text-xs"
                    >
                      Paneli Aç <ExternalLink size={13} />
                    </button>

                    {/* TEST ŞİRKETİNİ VERİTABANINDAN SİLME BUTONU */}
                    <button
                      onClick={() => handleDeleteCompany(comp.id, comp.name)}
                      title="Bu şirketi veritabanından sil"
                      className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}

            {(!selectedPartner.companies || selectedPartner.companies.length === 0) && (
              <div className="py-16 text-center rounded-3xl bg-white/[0.01] border border-white/10 text-gray-500 text-sm">
                Bu API partnerine bağlı henüz hiçbir şirket kaydı bulunmuyor.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 📊 EKRAN 5: TEKİL ŞİRKET KULLANIM & HARCADIKÇA KAZAN DETAY EKRANI     */}
      {/* (Kullanımları tamamen ayrı bir ekranda gösteren sade yapı)            */}
      {/* ===================================================================== */}
      {currentScreen === 'COMPANY_USAGE' && selectedPartner && selectedCompany && (
        <div className="flex-1 p-8 max-w-5xl mx-auto w-full space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-white/10">
            <div>
              <span className="text-xs text-indigo-400 font-bold">
                {selectedPartner.name} • Tekil Şirket Tüketim Raporu
              </span>
              <h2 className="text-2xl font-black mt-1">{selectedCompany.name}</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Vergi No: {selectedCompany.taxNumber} • B2B Eşleşme Kodu: {selectedCompany.b2bConnectCode}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-right">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-300 block">
                Bu Şirketin Anlık Hakedişi
              </span>
              <span className="text-2xl font-black text-emerald-400">
                {selectedCompany.billing?.totalRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </span>
            </div>
          </div>

          {/* 4 ANA KULLANIM KARTI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-indigo-400">
                <span className="text-[11px] font-black uppercase">Aktif Çalışan</span>
                <Users size={18} />
              </div>
              <div className="text-2xl font-black">{selectedCompany.activeUsersCount} Kişi</div>
              <div className="text-[11px] text-gray-400">
                Koltuk Bedeli: <b className="text-white">{selectedCompany.billing?.userSeatCost} ₺</b>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-emerald-400">
                <span className="text-[11px] font-black uppercase">Paylaşılan Dosya Boyutu</span>
                <FileSpreadsheet size={18} />
              </div>
              <div className="text-2xl font-black">{formatBytes(selectedCompany.storageUsedBytes)}</div>
              <div className="text-[11px] text-gray-400">
                Toplam <b>{selectedCompany.totalFilesCount}</b> Evrak • Bedel: <b className="text-white">{selectedCompany.billing?.storageCost} ₺</b>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-blue-400">
                <span className="text-[11px] font-black uppercase">Harcanan Veri Trafiği</span>
                <Database size={18} />
              </div>
              <div className="text-2xl font-black">{formatBytes(selectedCompany.bandwidthUsedBytes)}</div>
              <div className="text-[11px] text-gray-400">
                Toplam <b>{selectedCompany.totalMessagesCount}</b> Mesaj • Bedel: <b className="text-white">{selectedCompany.billing?.trafficCost} ₺</b>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2">
              <div className="flex items-center justify-between text-amber-400">
                <span className="text-[11px] font-black uppercase">Canlı Ses & Ekran</span>
                <Mic size={18} />
              </div>
              <div className="text-2xl font-black">{selectedCompany.voiceMinutesUsed} dk</div>
              <div className="text-[11px] text-gray-400">
                Ses Tüketim Bedeli: <b className="text-white">{selectedCompany.billing?.voiceCost} ₺</b>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* 📋 EKRAN 6: ŞİRKET VE KULLANICI BAZLI LOG MERKEZİ (KURUCU ÖZEL)       */}
      {/* ===================================================================== */}
      {currentScreen === 'COMPANY_LOGS' && selectedPartner && selectedCompany && (
        <div className="flex-1 p-8 max-w-6xl mx-auto w-full space-y-6">
          {/* ÜST BAŞLIK VE KATEGORİ FİLTRESİ */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-white/10">
            <div>
              <span className="text-xs text-emerald-400 font-bold">
                {selectedPartner.name} • Şirket & Kullanıcı Bazlı Denetim Kayıtları
              </span>
              <h2 className="text-2xl font-black mt-1">{selectedCompany.name} — Log Merkezi</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Sol menüden bir çalışana tıklayarak sadece o kullanıcının yaptığı işlemleri süzebilirsiniz.
              </p>
            </div>

            {/* Kategori Filtre Butonları */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: 'ALL', label: 'Tümü' },
                { id: 'FILE', label: '📁 Dosya/Evrak' },
                { id: 'EXTERNAL', label: '🌍 Dış Müşteri' },
                { id: 'CHANNEL', label: '💬 Odalar' },
                { id: 'ROLE', label: '👑 Yetki/Rol' },
                { id: 'SECURITY', label: '🚨 Güvenlik/Saldırı' }
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedLogCategory(cat.id);
                    fetchFounderLogs(selectedPartner.id, selectedCompany.id, selectedLogUserId, cat.id);
                  }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                    selectedLogCategory === cat.id
                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300'
                      : 'bg-white/[0.02] border-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* ANA GÖVDE: SOLDA KULLANICI LİSTESİ, SAĞDA LOG AKIŞI */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* SOL SÜTUN: ŞİRKETTEKİ KULLANICILAR (KULLANICI BAZLI AYRIM) */}
            <div className="lg:col-span-4 space-y-2">
              <div className="text-[11px] font-black uppercase tracking-wider text-gray-400 px-1 mb-2">
                Kullanıcıya Göre Filtrele ({companyUsersForLogs.length} Personel)
              </div>

              {/* 1. Tüm Şirket Çalışanları */}
              <button
                onClick={() => {
                  setSelectedLogUserId('ALL');
                  fetchFounderLogs(selectedPartner.id, selectedCompany.id, 'ALL', selectedLogCategory);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-2xl border text-left transition-all ${
                  selectedLogUserId === 'ALL'
                    ? 'bg-indigo-500/15 border-indigo-500/40 text-white'
                    : 'bg-white/[0.02] border-white/10 text-gray-300 hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Users size={16} className="text-indigo-400" />
                  <span className="text-xs font-black">Tüm Şirket Hareketleri</span>
                </div>
                <ChevronRight size={14} />
              </button>

              {/* 2. Sadece Dışarıdan Linkle Gelen Misafirler */}
              <button
                onClick={() => {
                  setSelectedLogUserId('GUESTS_ONLY');
                  fetchFounderLogs(selectedPartner.id, selectedCompany.id, 'GUESTS_ONLY', selectedLogCategory);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-2xl border text-left transition-all ${
                  selectedLogUserId === 'GUESTS_ONLY'
                    ? 'bg-amber-500/15 border-amber-500/40 text-white'
                    : 'bg-white/[0.02] border-white/10 text-gray-300 hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <ExternalLink size={16} className="text-amber-400" />
                  <span className="text-xs font-black">Dış Müşteri / Misafirler</span>
                </div>
                <ChevronRight size={14} />
              </button>

              <div className="pt-2 border-t border-white/10 space-y-1.5">
                {companyUsersForLogs.map((u: any) => (
                  <button
                    key={u.id}
                    onClick={() => {
                      setSelectedLogUserId(u.id);
                      fetchFounderLogs(selectedPartner.id, selectedCompany.id, u.id, selectedLogCategory);
                    }}
                    className={`w-full flex items-center justify-between p-2.5 rounded-2xl border text-left transition-all ${
                      selectedLogUserId === u.id
                        ? 'bg-emerald-500/15 border-emerald-500/40 text-white'
                        : 'bg-white/[0.02] border-white/10 text-gray-300 hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img src={u.avatarUrl} className="w-8 h-8 rounded-full object-cover shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold truncate">{u.username}</div>
                        <div className="text-[10px] text-gray-400 truncate">
                          {u.role?.name || u.roleTitle}
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={14} className="shrink-0 text-gray-500" />
                  </button>
                ))}
              </div>
            </div>

            {/* SAĞ SÜTUN: SEÇİLİ KULLANICININ VEYA ŞİRKETİN DETAYLI LOGLARI */}
            <div className="lg:col-span-8 space-y-3">
              {logsLoading ? (
                <div className="py-16 text-center text-xs text-gray-400">Log kayıtları yükleniyor...</div>
              ) : founderLogs.length === 0 ? (
                <div className="py-16 text-center rounded-3xl bg-white/[0.02] border border-white/10 text-gray-500 text-xs">
                  Seçili kullanıcı veya filtre için henüz bir hareket kaydı bulunmuyor.
                </div>
              ) : (
                founderLogs.map((log: any) => (
                  <div
                    key={log.id}
                    className={`p-4 rounded-2xl border flex items-start justify-between gap-4 ${
                      log.severity === 'CRITICAL'
                        ? 'bg-red-500/10 border-red-500/30'
                        : log.severity === 'WARN'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-white/[0.02] border-white/10'
                    }`}
                  >
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-black text-white">{log.title}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/10 text-indigo-300 font-bold">
                          👤 {log.actorName} {log.actorRole ? `(${log.actorRole})` : ''}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-black/40 text-gray-400 font-mono">
                          IP: {log.ipAddress || '127.0.0.1'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed">{log.description}</p>
                    </div>

                    <div className="text-right shrink-0 text-[10px] text-gray-400 font-mono">
                      <div>{new Date(log.createdAt).toLocaleDateString('tr-TR')}</div>
                      <div>{new Date(log.createdAt).toLocaleTimeString('tr-TR')}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

     {/* ===================================================================== */}
      {/* 📘 EKRAN 7: MÜŞTERİ GELİŞTİRİCİ KILAVUZU (API DOCS - KOPYALA YAPIŞTIR) */}
      {/* ===================================================================== */}
      {currentScreen === 'API_DOCS' && (
        <div className="flex-1 p-8 max-w-5xl mx-auto w-full space-y-6">
          <div className="border-b border-white/10 pb-4">
            <span className="text-xs text-indigo-400 font-bold uppercase tracking-wider">
              Sesgelio Studios • CPaaS Entegrasyon Rehberi
            </span>
            <h2 className="text-2xl font-black mt-1">Geliştirici Kılavuzu (API & Headless SDK Docs)</h2>
            <p className="text-xs text-gray-400 mt-1">
              Kendi tasarımını yapacak (Model C) veya hazır şablonlarımızı (Model A & B) kendi yazılımına bağlayacak müşterilere doğrudan bu dökümanı iletebilirsiniz.
            </p>
          </div>

          {/* ADIM 1: GÜVENLİ OTURUM BAŞLATMA */}
          <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black uppercase text-emerald-400">
                1. Adım: Arka Planda Güvenli Oturum Bileti Alma (Backend POST)
              </span>
              <span className="text-[11px] font-mono px-2.5 py-1 rounded bg-black/50 text-gray-300">
                POST https://sesgelio.com/api/b2b/sso-handshake
              </span>
            </div>
            <p className="text-xs text-gray-400">
              Müşterinin yazılımı, kendi içerisindeki aktif kullanıcı bilgilerini gizli <b>apiKey</b> ile bu adrese gönderir ve tek kullanımlık şifreli <b>token</b> alır (URL'de hiçbir zaman API Key veya Vergi No görünmez):
            </p>
            <pre className="p-4 rounded-2xl bg-black/70 border border-white/10 text-[11px] font-mono text-indigo-200 overflow-x-auto">
{`const response = await fetch("https://sesgelio.com/api/b2b/sso-handshake", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    apiKey: "SIZE_VERILEN_SESGELIO_API_KEY",
    externalCompanyId: "FIRMA_VERGI_NO_VEYA_ID",
    companyName: "Örnek Şirket A.Ş.",
    externalUserId: "kullanici_101",
    username: "Ahmet Yılmaz",
    roleTitle: "Muhasebe Müdürü",
    isCompanyAdmin: true, // Patron ise true, personel ise false
    hasPackage: true
  })
});
const { token } = await response.json();`}
            </pre>
          </div>

          {/* ADIM 2: MODEL A VE B İÇİN GÖMÜLÜ EKRAN AÇMA */}
          <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
            <span className="text-xs font-black uppercase text-indigo-400">
              2. Adım (Model A & B): Hazır veya Özel Tasarımı Gömme (WebView / Iframe)
            </span>
            <p className="text-xs text-gray-400">
              1. Adımda alınan <b>token</b> ile pencere açılır. Sayfa açıldığı anda adres çubuğundaki anahtar otomatik gizlenir:
            </p>
            <pre className="p-4 rounded-2xl bg-black/70 border border-white/10 text-[11px] font-mono text-emerald-200 overflow-x-auto">
{`<iframe
  src={\`https://sesgelio.com/embed?session=\${token}\`}
  allow="microphone; camera; display-capture; clipboard-write"
  style="width: 100%; height: 100%; border: none;"
/>`}
            </pre>
          </div>

          {/* ADIM 3: MODEL C (HEADLESS API - KENDİ TASARIMINI YAPANLAR) */}
          <div className="p-6 rounded-3xl bg-white/[0.02] border border-white/10 space-y-3">
            <span className="text-xs font-black uppercase text-amber-400">
              3. Adım (Model C - Headless API): Kendi Tasarımını Yapan Şirketler İçin Uç Noktalar
            </span>
            <p className="text-xs text-gray-400">
              Arayüzü kendi sunucusunda kodlayan şirketler, aldıkları <b>Bearer Token</b> ile doğrudan şu kapıları kullanır:
            </p>
            <pre className="p-4 rounded-2xl bg-black/70 border border-white/10 text-[11px] font-mono text-gray-200 overflow-x-auto">
{`// A) Anlık Mesajlaşma (Socket.io)
const socket = io("https://sesgelio.com", { path: "/b2b-socket/", auth: { token } });
socket.emit("join-b2b-channel", channelId);
socket.emit("send-b2b-message", { channelId, content: "Merhaba!" });

// B) Kurumsal Dosya / Evrak Yükleme (REST API)
POST https://sesgelio.com/api/b2b/channels/:channelId/upload
Headers: { Authorization: "Bearer " + token } (FormData: file)

// C) 256 kbps Ses & 2K 60 FPS Ekran Paylaşımı Bileti (LiveKit WebRTC)
POST https://sesgelio.com/api/b2b/voice/token
Headers: { Authorization: "Bearer " + token }
Body: { "channelId": "ODA_ID" } -> Dönen { token, livekitUrl } ile odaya bağlanılır.`}
            </pre>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* ➕ YENİ API PARTNERİ OLUŞTURMA MODALI (3 MODEL + KALİTE PAKETLERİ)     */}
      {/* ===================================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={handleCreatePartner}
            className="bg-[#0e1017] border border-white/15 rounded-3xl p-6 w-full max-w-xl space-y-4 shadow-2xl my-auto"
          >
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-base font-black uppercase flex items-center gap-2">
                <Sparkles size={18} className="text-[#ff2b5e]" /> Yeni API Partneri & Paket Tanımla
              </h3>
              <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 mb-1">Yazılım / Şirket Adı</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: TrendERP Yazılım"
                  value={newPartnerName}
                  onChange={e => setNewPartnerName(sanitizeInput(e.target.value))}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 mb-1">Panel Başlığı</label>
                <input
                  type="text"
                  placeholder="Örn: TrendERP İletişim"
                  value={newBrandTitle}
                  onChange={e => setNewBrandTitle(sanitizeInput(e.target.value))}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                />
              </div>
            </div>

            {/* 1. SEÇİM: 3 NET TESLİMAT VE TASARIM MODELİ */}
            <div>
              <label className="block text-[11px] font-bold text-gray-400 mb-1.5">
                1. Teslimat & Tasarım Modeli Seçin
              </label>
              <div className="space-y-2">
                {Object.entries(TEMPLATE_META).map(([key, item]) => (
                  <div
                    key={key}
                    onClick={() => setNewTemplateId(key)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                      newTemplateId === key
                        ? 'bg-indigo-500/15 border-indigo-500/50'
                        : 'bg-black/30 border-white/5 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-white">{item.name}</span>
                      <span className="text-[10px] font-bold text-indigo-300">{item.setupNote}</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. SEÇİM: SES VE EKRAN PAYLAŞIMI KALİTE PAKETİ (256 KBPS / 2K 60 FPS) */}
            <div>
              <label className="block text-[11px] font-bold text-gray-400 mb-1.5">
                2. Ses & Ekran Paylaşımı Kalite Paketi (Tier)
              </label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(QUALITY_TIERS).map(([tierKey, tier]) => (
                  <div
                    key={tierKey}
                    onClick={() => {
                      setNewQualityTier(tierKey);
                      setNewUserPrice(tier.defaultSeatPrice);
                      setNewStoragePrice(tier.defaultStoragePrice);
                    }}
                    className={`p-2.5 rounded-xl border cursor-pointer transition-all text-left ${
                      newQualityTier === tierKey
                        ? 'bg-white/10 border-amber-400/60 shadow-md'
                        : 'bg-black/30 border-white/5 hover:bg-white/5'
                    }`}
                  >
                    <div className="text-xs font-black">{tier.name}</div>
                    <div className="text-[10px] text-emerald-400 font-bold mt-1">{tier.audioSpec}</div>
                    <div className="text-[10px] text-indigo-300 font-bold">{tier.videoSpec}</div>
                    <div className="text-[9px] text-gray-400 mt-0.5">{tier.uploadLimit}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. FİYATLANDIRMA VE RENK */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 mb-1">Vurgu Rengi</label>
                <input
                  type="color"
                  value={newPrimaryColor}
                  onChange={e => setNewPrimaryColor(e.target.value)}
                  className="w-full h-9 rounded-xl bg-black/50 border border-white/10 cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 mb-1">Koltuk (₺ / Kişi)</label>
                <input
                  type="number"
                  value={newUserPrice}
                  onChange={e => setNewUserPrice(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 mb-1">Depolama (₺ / GB)</label>
                <input
                  type="number"
                  value={newStoragePrice}
                  onChange={e => setNewStoragePrice(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
              <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-xs text-gray-400">
                Vazgeç
              </button>
              <button type="submit" className="px-5 py-2 rounded-xl bg-[#ff2b5e] text-white text-xs font-black uppercase">
                Oluştur
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SesgelioStudiosDashboard;